import PropTypes from "prop-types"
import Icon from "components/planning_center_icon"
import AttendanceDropdown from "components/admin/AttendanceDropdown/AttendanceDropdown"
import { css } from "@emotion/core"
import { Button } from "@planningcenter/tapestry-react"
import { pluralize, isPresent } from "utils/helpers"
import { api } from "utils/api"
import { createRegistrationPaths } from "./eventHelpers"
import { Menu, MenuButton, MenuList, MenuLink } from "@reach/menu-button"
import { Pane } from "components/ui"
import useApiAction from "hooks/use_api_action"
import useApiResource from "hooks/use_api_resource"
import useFlipperFlag from "hooks/use_flipper_flag"

const EventHeader = ({
  attendeeCount,
  assignmentTypes,
  event,
  apiBaseUrl,
  checkInsBaseUrl,
  baseParams,
  filterParams,
  isActingAsStaff,
  subscribedToCheckIns,
  checkInsSettingsPath
}) => {
  const detailedWaitlistNotifyEnabled = useFlipperFlag("ROLLOUT_detailed_waitlist_notify")
  const notifyWaitlist = useApiAction({
    path: `events/${event.id}/notify_waitlist`
  })

  const handleWaitlistNotification = () => {
    return alertModal(
      {
        title: "Notify waitlist?",
        text:
          "This will send an email to each attendee on the waitlist whose selection type has capacity.",
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "No, don't notify!",
        confirmButtonText: "Yes, notify them all!",
        customClass: "warning",
        closeOnConfirm: true
      },
      () => {
        notifyWaitlist.run({ mode: "all" })
      }
    )
  }

  const handleCancellation = () => {
    return alertModal(
      {
        title: "Cancel all registrations?",
        text:
          "Any payments will not be automatically refunded. Depending on the number of registrations, it may take a few minutes for all cancelations to take effect.",
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "No, don't cancel them",
        confirmButtonText: "Yes, cancel all of them!",
        customClass: "destructive-warning",
        closeOnConfirm: true
      },
      () => {
        api.post(event.paths.cancelEvent).then(_ => {
          alertModal({
            title: "Canceling!",
            text:
              "We are in the process of canceling all of the registrations for this signup. This may take a few minutes.",
            type: "success",
            confirmButtonText: "Ok"
          })
        })
      }
    )
  }

  const handleDeletion = () => {
    return alertModal(
      {
        title: "Delete attendance report?",
        text: "This cannot be undone.",
        type: "error",
        showCancelButton: true,
        cancelButtonText: "No, don't delete it!",
        confirmButtonText: "Yes, delete it!",
        customClass: "warning",
        closeOnConfirm: true
      },
      () =>
        useApiResource({
          path: `events/${event.id}/attendance_reports/`
        })
          .destroy(baseParams.attendance_report_id)
          .then(() => Turbolinks.visit(event.paths.show))
    )
  }

  const renderBalanceDue = () => {
    return event.registrationsWithBalanceDueCount !== 0 ? (
      <MenuLink data-modal-url={event.paths.balanceDueEmail} className="dropdown-menu-item w-100%">
        Balance due reminder
      </MenuLink>
    ) : (
      <span className="dropdown-menu-item disabled">Balance due reminder</span>
    )
  }

  const renderFormDue = () => {
    return event.registrationsWithFormDueCount !== 0 ? (
      <MenuLink data-modal-url={event.paths.formDueEmail} className="dropdown-menu-item w-100%">
        Incomplete external form reminder
      </MenuLink>
    ) : (
      <span className="dropdown-menu-item disabled">Incomplete external form reminder</span>
    )
  }

  const renderAttendeeInfo = () => {
    return event.registrationsWithMissingAttendeeInfoCount !== 0 ? (
      <MenuLink
        data-modal-url={event.paths.attendeeInfoEmail}
        className="dropdown-menu-item w-100%"
      >
        Incomplete attendee info reminder
      </MenuLink>
    ) : (
      <span className="dropdown-menu-item disabled">Incomplete attendee info reminder</span>
    )
  }

  const renderNotifyWaitlist = () => {
    return event.hasAvailabilityForAnyWaitlistedAttendee ? (
      <MenuLink
        className="dropdown-menu-item w-100%"
        onClick={handleWaitlistNotification}
        style={{
          color: "#c74e41 !important",
          padding: "0 1rem !important"
        }}
      >
        Notify waitlist of available capacity
      </MenuLink>
    ) : (
      <span className="dropdown-menu-item disabled">Notify waitlist of available capacity</span>
    )
  }

  const showActions =
    !baseParams.show_attendance && !baseParams.show_waitlist && !baseParams.show_canceled
  const showWaitlistNotify = baseParams.show_waitlist && detailedWaitlistNotifyEnabled

  return (
    <Pane.Header padding={1} paddingLeft={2} flexWrap="wrap">
      <Pane.Title className="fw-n">
        {isPresent(attendeeCount) ? pluralize("attendee", "attendees", attendeeCount) : null}
      </Pane.Title>
      {baseParams.show_attendance && (
        <div>
          <Button
            title="Delete"
            size="md"
            theme="error"
            variant="fill"
            onClick={handleDeletion}
            marginHorizontal={1}
          />
          <a
            href={`${event.paths.attendanceReport}/${baseParams.attendance_report_id}`}
            className="mb-1 mb-0@sm action-btn btn2"
          >
            Edit attendance
          </a>
        </div>
      )}
      {!baseParams.show_attendance && (
        <div className="fw-w d-f@sm">
          {showActions && (
            <>
              <a
                href={event.paths.newReservation}
                className="mb-1 mb-0@sm mr-1@sm w-100% w-a@sm create-btn btn2"
              >
                New registration
              </a>
              <AttendanceDropdown
                event={event}
                isSimpleEvent={event.isSimple}
                isActingAsStaff={isActingAsStaff}
                eventAttendanceReportPath={event.paths.attendanceReport}
                assignmentTypes={assignmentTypes}
                apiBaseUrl={apiBaseUrl}
                checkInsBaseUrl={checkInsBaseUrl}
                attendeeCounts={{ activeCount: attendeeCount }}
                baseParams={baseParams}
                filterParams={filterParams}
                subscribedToCheckIns={subscribedToCheckIns}
                checkInsSettingsPath={checkInsSettingsPath}
              />
            </>
          )}

          <Menu>
            <MenuButton className="action-btn btn2 w-100% w-a@sm">
              <Icon symbol="general#cog" />
              Actions
              <span data-reach-menu-button-toggle-icon style={{ margin: "0 0 0 4px" }}>
                <Icon symbol="general#down-caret" style={{ margin: 0 }} />
              </span>
            </MenuButton>

            <MenuList style={{ whiteSpace: "normal", width: 240 }}>
              <div className="pt-2 font-size-5 pb-3p px-2 tt-u fw-600 c-neutral-700">Email</div>
              <MenuLink
                className="dropdown-menu-item w-100%"
                data-modal-url={
                  createRegistrationPaths(
                    {
                      url: event.paths.newEmail,
                      request: event.paths.newEmail
                    },
                    baseParams,
                    filterParams
                  ).requestPath
                }
              >
                General email
              </MenuLink>
              {showWaitlistNotify && renderNotifyWaitlist()}
              {!event.isSimple && showActions && renderAttendeeInfo()}
              {showActions && renderBalanceDue()}
              {!event.isSimple && showActions && renderFormDue()}
              <div className="mt-1 pt-2 font-size-5 pb-3p px-2 tt-u fw-600 line c-neutral-700">
                Export
              </div>
              {!event.isSimple && showActions && (
                <>
                  <MenuLink
                    className="dropdown-menu-item"
                    target="_blank"
                    rel="noreferrer"
                    href={event.paths.reportOverview}
                  >
                    Overview
                  </MenuLink>
                  <MenuLink
                    className="dropdown-menu-item"
                    target="_blank"
                    rel="noreferrer"
                    href={event.paths.reportGuestList}
                  >
                    Guest List
                  </MenuLink>
                </>
              )}
              <MenuLink
                className="dropdown-menu-item"
                href={
                  createRegistrationPaths(
                    {
                      url: event.paths.reportCSV,
                      request: event.paths.reportCSV
                    },
                    baseParams,
                    filterParams
                  ).requestPath
                }
              >
                CSV
              </MenuLink>
              {showActions && (event.hasActiveAttendees || event.isSimple) && (
                <div className="mt-1 p-1 line">
                  <MenuLink
                    className="quiet-destroy-btn btn2 mb-0!"
                    onClick={handleCancellation}
                    style={{
                      color: "var(--t-text-color-status-error) !important",
                      padding: "0 1rem !important"
                    }}
                    css={destroyButtonImportant}
                  >
                    Cancel all registrations
                  </MenuLink>
                </div>
              )}
            </MenuList>
          </Menu>
        </div>
      )}
    </Pane.Header>
  )
}

const destroyButtonImportant = css`
  color: var(--t-text-color-status-error) !important;
  padding: 0 1rem !important;
`

EventHeader.propTypes = {
  attendeeCount: PropTypes.number,
  assignmentTypes: PropTypes.array,
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isSimple: PropTypes.bool,
    hasActiveAttendees: PropTypes.bool,
    hasAvailabilityForAnyWaitlistedAttendee: PropTypes.bool,
    registrationsWithBalanceDueCount: PropTypes.number,
    registrationsWithFormDueCount: PropTypes.number,
    registrationsWithMissingAttendeeInfoCount: PropTypes.number,
    paths: PropTypes.shape({
      cancelEvent: PropTypes.string,
      balanceDueEmail: PropTypes.string,
      formDueEmail: PropTypes.string,
      attendeeInfoEmail: PropTypes.string,
      attendanceReport: PropTypes.string,
      newReservation: PropTypes.string,
      newRegistration: PropTypes.string,
      newEmail: PropTypes.string,
      reportOverview: PropTypes.string,
      reportGuestList: PropTypes.string,
      reportCSV: PropTypes.string,
      convertSignup: PropTypes.string,
      show: PropTypes.string
    })
  }),
  apiBaseUrl: PropTypes.string,
  checkInsBaseUrl: PropTypes.string,
  baseParams: PropTypes.shape({
    show_waitlist: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    show_canceled: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    show_attendance: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    attendance_report_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  filterParams: PropTypes.shape({}),
  isActingAsStaff: PropTypes.bool,
  subscribedToCheckIns: PropTypes.bool,
  checkInsSettingsPath: PropTypes.string
}

export default EventHeader
